import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { DIRECTION } from '../common/direction.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { BaseScene } from './base-scene.js';
import { netManager } from '../net/index.js';
import { SKIN_KEYS, skinIdleFrame, resolveSkinKey } from '../world/characters/skins.js';
import { readProfile, writeProfile, applyAccountToDataManager } from '../world/profile.js';
import { makeButton, makeCloseButton } from '../utils/ui-button.js';
import { fetchMyRank } from '../net/api.js';

/**
 * @typedef ProfileSceneData
 * @type {object}
 * @property {string} previousSceneName
 */

/**
 * In-world account profile page (overlay, pauses the world). Shows PFP, name, bio, gold, level,
 * and party. Editable: name + PFP + bio. Saving → POST /player/profile → persists to the db and
 * updates the local data-manager so the world + HUD reflect the change. Back (ESC) returns.
 */
export class ProfileScene extends BaseScene {
  /** @type {ProfileSceneData} */
  #sceneData;
  /** @type {number} */
  #skinIndex;
  /** @type {Phaser.GameObjects.Sprite | undefined} */
  #pfp;
  /** @type {HTMLInputElement | undefined} */
  #nameInput;
  /** @type {HTMLTextAreaElement | undefined} */
  #bioInput;
  /** @type {Phaser.GameObjects.Text} */
  #statusText;
  /** @type {boolean} */
  #busy;
  /** @type {import('../net/api.js').PlayerRank | null} */
  #myRank = null;
  /** @type {number} design-space left edge of the panel (for DOM ↔ design mapping) */
  #designLeft = 0;
  /** @type {number} design-space top edge of the panel */
  #designTop = 0;
  /** @type {number} current camera zoom (contain-fit factor) */
  #fit = 1;

  constructor() {
    super({ key: SCENE_KEYS.PROFILE_SCENE });
  }

  /** @param {ProfileSceneData} data @returns {void} */
  init(data) {
    super.init(data);
    this.#sceneData = data;
    this.#busy = false;
    const current = resolveSkinKey(dataManager.store.get(DATA_MANAGER_STORE_KEYS.PLAYER_AVATAR));
    this.#skinIndex = Math.max(0, SKIN_KEYS.indexOf(current));
  }

  /** @returns {void} */
  create() {
    super.create();
    const { width, height } = this.scale;
    const profile = readProfile();

    // dimmed backdrop over the paused world. Drawn oversized + centered so it still covers the view
    // once the camera zooms out to fit the fixed design on a narrow phone (see #fitToViewport).
    this.add.rectangle(width / 2, height / 2, width * 4, height * 4, 0x05060d, 0.78).setOrigin(0.5);

    // Fixed-size premium panel (a two-column layout that needs ~900px); the camera contain-fit below
    // scales the WHOLE design — Phaser + the DOM inputs — down to fit a phone, so it never overflows.
    const panelW = 900;
    const panelH = 560;
    const px = width / 2 - panelW / 2;
    const py = height / 2 - panelH / 2;
    this.#designLeft = px;
    this.#designTop = py;
    this.add.rectangle(width / 2, height / 2, panelW, panelH, 0x10121e, 0.96).setStrokeStyle(2, 0x6a3df0);

    this.add
      .text(width / 2, py + 36, 'PROFILE', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: '#ffffff',
        stroke: '#6a3df0',
        strokeThickness: 5,
      })
      .setOrigin(0.5);

    // left column: PFP + avatar cycling
    const pfpX = px + 150;
    const pfpY = py + 190;
    this.add.rectangle(pfpX, pfpY, 200, 200, 0x161827, 0.95).setStrokeStyle(2, 0x342f55);
    const arrowLeft = this.add
      .text(pfpX - 86, pfpY, '◄', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '30px', color: '#9aa6c9' })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    arrowLeft.on(Phaser.Input.Events.POINTER_DOWN, () => this.#cycleAvatar(-1));
    const arrowRight = this.add
      .text(pfpX + 86, pfpY, '►', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '30px', color: '#9aa6c9' })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    arrowRight.on(Phaser.Input.Events.POINTER_DOWN, () => this.#cycleAvatar(1));
    if (this.textures.exists(SKIN_KEYS[this.#skinIndex])) {
      this.#pfp = this.add
        .sprite(pfpX, pfpY, SKIN_KEYS[this.#skinIndex], skinIdleFrame(DIRECTION.DOWN))
        .setOrigin(0.5)
        .setScale(3.2);
    }
    this.add
      .text(pfpX, pfpY + 122, '← →  change avatar', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: '#9aa6c9',
      })
      .setOrigin(0.5);

    // wallet (short) + gold/level stats under the PFP
    const wallet = netManager.wallet;
    const short = wallet ? `${wallet.slice(0, 4)}…${wallet.slice(-4)}` : 'no wallet';
    const level = this.#partyTopLevel();
    this.add
      .text(
        pfpX,
        pfpY + 156,
        `${short}\nGold ${profile.gold}     Lvl ${level}`,
        { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '18px', color: '#bda9ff', align: 'center' }
      )
      .setOrigin(0.5);

    // right column: editable name + bio (DOM) + party list
    this.#buildDomFields(profile, px + panelW * 0.58, py);
    this.#buildPartyList(px + panelW * 0.42, py + 300, panelW * 0.5);

    // Contain-fit the fixed 900×560 design into the real viewport (centered), then map the DOM
    // inputs through the same transform so they track the Phaser fields. Re-runs on resize.
    this.#fitToViewport(panelW, panelH);
    this.#layoutDom();
    const onResize = () => {
      this.#fitToViewport(panelW, panelH);
      this.#layoutDom();
    };
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, onResize, this));

    // status + hints
    this.#statusText = this.add
      .text(width / 2, py + panelH - 64, '', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '18px',
        color: '#7fffa0',
      })
      .setOrigin(0.5);
    this.add
      .text(width / 2, py + panelH - 32, 'SPACE / Save  ·  ESC / ✕  back', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: '#9aa6c9',
      })
      .setOrigin(0.5);

    // mouse-driven Save + close
    makeButton(this, {
      x: width / 2 - 124,
      y: py + panelH - 96,
      width: 200,
      height: 46,
      label: 'SAVE',
      primary: true,
      onClick: () => void this.#save(),
    });
    // 🐦 Share on X (rank fetched async; shares gold-only until it lands).
    makeButton(this, {
      x: width / 2 + 124,
      y: py + panelH - 96,
      width: 200,
      height: 46,
      label: '🐦 Flex on X',
      onClick: () => this.#shareOnX(),
    });
    makeCloseButton(this, px + panelW - 34, py + 34, () => this.#close());

    void this.#loadRank();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.#destroyDomFields());
  }

  /**
   * @param {{ name: string; bio: string }} profile
   * @param {number} centerX screen x to center the inputs on
   * @param {number} panelTop
   * @returns {void}
   */
  #buildDomFields(profile, centerX, panelTop) {
    // Store the fields' DESIGN-space geometry (center x, top y, width/height) so #layoutDom can map
    // them to screen px through the camera zoom. Sizes are in design px (scaled by #fit at layout).
    this.#nameDesign = { cx: centerX, top: panelTop + 110, w: 380, h: 48 };
    this.#bioDesign = { cx: centerX, top: panelTop + 172, w: 380, h: 120 };

    const baseCss = (extra = '') =>
      [
        'position:fixed',
        'box-sizing:border-box',
        'border-radius:10px',
        'border:1px solid rgba(154,123,255,0.35)',
        'background:rgba(16,18,30,0.85)',
        'color:#fff',
        'outline:none',
        'font-family:system-ui,sans-serif',
        'z-index:1000',
        extra,
      ].join(';');

    const name = document.createElement('input');
    name.type = 'text';
    name.maxLength = 24;
    name.placeholder = 'Username';
    name.value = profile.name;
    name.style.cssText = baseCss('text-align:center');
    name.addEventListener('keydown', (e) => e.stopPropagation());
    document.body.appendChild(name);
    this.#nameInput = name;

    const bio = document.createElement('textarea');
    bio.maxLength = 280;
    bio.rows = 4;
    bio.placeholder = 'Bio';
    bio.value = profile.bio;
    bio.style.cssText = baseCss('resize:none');
    bio.addEventListener('keydown', (e) => e.stopPropagation());
    document.body.appendChild(bio);
    this.#bioInput = bio;

    this.#layoutDom();
  }

  /** @type {{cx:number,top:number,w:number,h:number}|undefined} */ #nameDesign;
  /** @type {{cx:number,top:number,w:number,h:number}|undefined} */ #bioDesign;

  /**
   * Contain-fit the fixed-size profile design into the real viewport via a centered camera zoom, so
   * the wide two-column panel never overflows a phone. Symmetric around `width/2`, so a uniform zoom
   * about the center keeps everything on-screen. @param {number} designW @param {number} designH
   * @returns {void}
   */
  #fitToViewport(designW, designH) {
    const vw = this.scale.width;
    const vh = this.scale.height;
    this.#fit = Math.min(1, (vw - 16) / designW, (vh - 16) / designH);
    const cam = this.cameras.main;
    cam.setZoom(this.#fit);
    cam.centerOn(vw / 2, vh / 2);
  }

  /**
   * Position the DOM inputs (which live OUTSIDE the Phaser camera transform) so they visually align
   * with their design-space slots under the current camera zoom. Maps design px → canvas screen px.
   * @returns {void}
   */
  #layoutDom() {
    if (!this.#nameInput || !this.#bioInput || !this.#nameDesign || !this.#bioDesign) {
      return;
    }
    const canvas = this.game.canvas;
    const bounds = canvas.getBoundingClientRect();
    const cam = this.cameras.main;
    const z = cam.zoom;
    // Visible world rect under the centered zoom. Computed explicitly (not read from cam.worldView,
    // which only updates at render time and would be stale right after setZoom/centerOn this frame):
    // the camera is centered on (vw/2, vh/2), so the top-left world point is center − halfView/zoom.
    const viewX = this.scale.width / 2 - cam.width / 2 / z;
    const viewY = this.scale.height / 2 - cam.height / 2 / z;
    const place = (el, d) => {
      const screenW = d.w * z;
      const screenH = d.h * z;
      const screenX = bounds.left + (d.cx - viewX) * z; // center x
      const screenY = bounds.top + (d.top - viewY) * z; // top y
      el.style.left = `${screenX}px`;
      el.style.top = `${screenY}px`;
      el.style.width = `${screenW}px`;
      el.style.height = `${screenH}px`;
      el.style.transform = 'translateX(-50%)';
      el.style.padding = `${Math.max(6, 10 * z)}px ${Math.max(8, 12 * z)}px`;
      el.style.fontSize = `${Math.max(13, Math.round(17 * z))}px`;
    };
    place(this.#nameInput, this.#nameDesign);
    place(this.#bioInput, this.#bioDesign);
  }

  /**
   * @param {number} centerX
   * @param {number} y
   * @param {number} width
   * @returns {void}
   */
  #buildPartyList(centerX, y, width) {
    /** @type {import('../types/typedef.js').Monster[]} */
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || [];
    this.add
      .text(centerX, y, 'PARTY', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: '#bda9ff',
      })
      .setOrigin(0.5);

    if (party.length === 0) {
      this.add
        .text(centerX, y + 30, 'No monsters yet.', {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '16px',
          color: '#9aa6c9',
        })
        .setOrigin(0.5);
      return;
    }

    const names = party
      .slice(0, 6)
      .map((m) => `${m.name ?? 'Monster'} Lv${m.currentLevel ?? 1}`)
      .join('   ·   ');
    this.add
      .text(centerX, y + 28, names, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '16px',
        color: '#e7e3ff',
        align: 'center',
        wordWrap: { width },
        lineSpacing: 4,
      })
      // Top-anchored so a wrapped (multi-line) party list grows DOWNWARD and never overlaps the
      // "PARTY" label or the bio above it (a centered origin made tall lists collide — "scuffed").
      .setOrigin(0.5, 0);
  }

  /** @returns {number} */
  #partyTopLevel() {
    /** @type {import('../types/typedef.js').Monster[]} */
    const party = dataManager.store.get(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY) || [];
    return party.reduce((max, m) => Math.max(max, m.currentLevel ?? 1), 1);
  }

  /** @returns {void} */
  #destroyDomFields() {
    this.#nameInput?.remove();
    this.#bioInput?.remove();
    this.#nameInput = undefined;
    this.#bioInput = undefined;
  }

  /** @returns {boolean} */
  #isTyping() {
    const active = document.activeElement;
    return active === this.#nameInput || active === this.#bioInput;
  }

  /** @param {number} delta @returns {void} */
  #cycleAvatar(delta) {
    this.#skinIndex = (this.#skinIndex + delta + SKIN_KEYS.length) % SKIN_KEYS.length;
    const key = SKIN_KEYS[this.#skinIndex];
    if (this.#pfp && this.textures.exists(key)) {
      this.#pfp.setTexture(key, skinIdleFrame(DIRECTION.DOWN));
    }
  }

  /** @returns {void} */
  update() {
    super.update();
    if (this._controls.isInputLocked || this.#busy) return;

    if (this._controls.wasBackKeyPressed()) {
      this.#close();
      return;
    }

    if (!this.#isTyping()) {
      const dir = this._controls.getDirectionKeyJustPressed();
      if (dir === DIRECTION.LEFT) this.#cycleAvatar(-1);
      else if (dir === DIRECTION.RIGHT) this.#cycleAvatar(1);

      if (this._controls.wasSpaceKeyPressed()) {
        void this.#save();
      }
    }
  }

  /**
   * Persist name/PFP/bio to the api, then sync the data-manager + HUD. Saves locally either way
   * so the change is never lost offline.
   * @returns {Promise<void>}
   */
  async #save() {
    if (this.#busy) return;
    this.#busy = true;
    this.#statusText.setColor('#e7e3ff').setText('Saving…');

    const username = (this.#nameInput?.value ?? '').trim();
    const bio = (this.#bioInput?.value ?? '').trim();
    const avatar = SKIN_KEYS[this.#skinIndex];

    // optimistic local write so the world/HUD reflect the edit immediately
    writeProfile({ name: username || undefined, avatar, bio });

    const view = await netManager.saveProfile({
      username: username || undefined,
      avatar,
      bio,
    });
    if (view) {
      applyAccountToDataManager(view);
      this.#statusText.setColor('#7fffa0').setText('Saved.');
    } else {
      this.#statusText.setColor('#ffd479').setText('Saved locally (offline).');
    }
    this.#busy = false;
  }

  /** Fetch the player's leaderboard rank so the share tweet can include it (best-effort). */
  async #loadRank() {
    this.#myRank = await fetchMyRank(netManager.wallet);
  }

  /** Open a prefilled tweet flexing the player's rank (if known) + gold in a new tab. */
  #shareOnX() {
    const gold = (this.#myRank?.gold ?? dataManager.getGold() ?? 0).toLocaleString();
    const rankPart = this.#myRank ? `rank #${this.#myRank.rank} ` : '';
    const text =
      `I'm ${rankPart}on Monstara with ${gold} Gold 🏆 ` +
      `Catch monsters, earn Gold, redeem REAL graded Pokémon cards. Play free 👉 monstara.fun @PlayMonstara`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  }

  /** @returns {void} */
  #close() {
    this.#destroyDomFields();
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.scene.stop(SCENE_KEYS.PROFILE_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (this.scene.isSleeping(prev)) {
      // Slept previous scene: wake (not restart) it, otherwise handleSceneResume never fires
      // and the player stays input-locked.
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
    // Always release input — only the resume() path fires handleSceneResume.
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
